import { Matrix4 } from '@math.gl/core'
import { ForwardedRef, forwardRef, useContext, useMemo } from 'react'
import { Position } from 'types'
import { MediaSprite } from '../../../types'
import { Renderable } from '../classes/renderer'
import Shader from '../classes/shader'
import { ArtboardContext } from '../context'
import { useTexture } from '../hooks/use-texture'
import { ArtboardSpriteLayerProps } from '../types'
import { resizeSprite } from '../utilities/resize-sprite'
import ArtboardLayer from './base'

const ArtboardSprite = (
    { mirrored = false, reversed = false, source, ...rest }: ArtboardSpriteLayerProps,
    ref: ForwardedRef<Renderable>
) => {
    const { frames, renderer } = useContext(ArtboardContext)

    const sprite: MediaSprite | null = useMemo(() => {
        if (!renderer || !source) {
            return null
        }

        return resizeSprite(source, renderer.gl.MAX_TEXTURE_SIZE)
    }, [renderer, source])

    const texture = useTexture(renderer, sprite?.canvas)

    function onDraw(position: Position, shader: Shader) {
        if (!source || !texture) return

        const { columns, frameIndexes, rows } = source
        const loops = Math.max(1, Math.floor(frames / frameIndexes.length))
        let progress = (shader.progress * loops) % 1

        if (reversed) {
            progress = 1 - progress
        }

        const frame = frameIndexes[Math.min(Math.floor(progress * frameIndexes.length), frameIndexes.length - 1)]
        const frameColumn = frame % columns
        const frameRow = Math.floor(frame / columns)
        const clipMatrix = new Matrix4()

        clipMatrix.translate([frameColumn / columns, frameRow / rows, 0])
        clipMatrix.scale([1 / columns, 1 / rows, 1])

        shader.drawImage({ ...position, mirrored, texture, clipMatrix })
    }

    return <ArtboardLayer {...rest} ref={ref} onDraw={onDraw} />
}

export default forwardRef(ArtboardSprite)
